import { Pipe, PipeTransform } from '@angular/core';
import { FormattedPercentPipe } from './formatted-percent.pipe';
import { Observable } from 'rxjs';
import { AbbreviateNumberPipe } from './abbreviate-number.pipe';

/**
 * `FormatDeciderPipe` is for deciding the appropriate formatting for a given value.
 * This pipe primarily serves to make HTML templates cleaner by reducing the need for conditional logic.
 * Currently, it supports percentage and abbreviated number formats.
 *
 * In the future, this class may be extended to include additional formatting options, such as currency.
 *
 * @example
 * // In HTML template
 * {{ rawValue | formatDecider:value }}
 *
 * @example
 * // Output could be "1.50M" or "10%"
 */
@Pipe({
    name: 'formatDecider',
    standalone: true
})
export class FormatDeciderPipe implements PipeTransform {
    private noDigitsInfoFormat = '1.0-0';
    constructor(
        private abbreviateNumberPipe: AbbreviateNumberPipe,
        private formattedPercentPipe: FormattedPercentPipe
    ) {}

    /**
     * Transforms the given raw value based on the value type (percentage or plain number).
     *
     * @param {string} value - If it includes '%', percentage formatting is applied.
     * @param {number} rawValue - The raw number value to be formatted.
     * @param {boolean} [useShortNumberFormat=false] - Whether to use short number format for percentages.
     * @returns {Observable<string>} An Observable that emits the appropriately formatted string.
     */
    transform(
        value: string,
        rawValue: number,
        useShortNumberFormat = false
    ): Observable<string> {
        if (value.includes('%')) {
            //This check is really about seeing if the string includes a '%' symbol, not about determining if the value is a percentage.
            // If the value is 0.01 and doesn't have a '%' although it is still displaying a percent value,
            // the other pipe will execute and give the expected output.
            return this.formattedPercentPipe.transform(
                rawValue,
                null,
                useShortNumberFormat ? this.noDigitsInfoFormat : null
            );
        } else if (useShortNumberFormat) {
            return this.abbreviateNumberPipe.transform(rawValue);
        }
    }
}
